import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, DollarSign, Calendar, ShieldCheck, Search, 
  Loader2, CheckCircle, XCircle, Trash2, Crown, BarChart3,
  Settings, UserCheck, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadData = async () => {
    try {
      const [statsRes, usersRes, bookingsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/users?page=1&limit=20`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/bookings?page=1&limit=20`, { headers: getAuthHeaders() })
      ]);

      // Check for access denied (404 = admin panel hidden)
      if (statsRes.status === 403 || usersRes.status === 403 || 
          statsRes.status === 404 || usersRes.status === 404) {
        // Redirect to 404 page - makes it look like admin panel doesn't exist
        navigate('/not-found', { replace: true });
        return;
      }

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
        setTotalPages(data.pages || 1);
        setTotalUsers(data.total || 0);
      }
      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        setBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (page = currentPage) => {
    setPageLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (searchQuery) params.append('search', searchQuery);
      if (userTypeFilter) params.append('user_type', userTypeFilter);
      if (sortField) params.append('sort_field', sortField);
      if (sortDirection) params.append('sort_direction', sortDirection);

      const res = await fetch(`${API_URL}/api/admin/users?${params}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
        setTotalPages(data.pages || 1);
        setTotalUsers(data.total || 0);
        setCurrentPage(page);
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setPageLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  // Re-fetch when sort changes
  useEffect(() => {
    if (!loading && user) {
      searchUsers(1);
    }
  }, [sortField, sortDirection]);

  // Export users to CSV
  const exportToCSV = async () => {
    try {
      toast.info('Preparing export...');
      
      // Fetch all users for export
      const params = new URLSearchParams({ page: 1, limit: 1000 });
      if (searchQuery) params.append('search', searchQuery);
      if (userTypeFilter) params.append('user_type', userTypeFilter);
      
      const res = await fetch(`${API_URL}/api/admin/users?${params}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch users');
      
      const data = await res.json();
      const allUsers = data.users || [];
      
      // Create CSV content
      const headers = ['#', 'Name', 'Email', 'Phone', 'Type', 'Location', 'Source', 'Status', 'Joined'];
      const rows = allUsers.map((u, idx) => [
        idx + 1,
        `"${u.name || ''}"`,
        `"${u.email || ''}"`,
        `"${u.phone || ''}"`,
        u.user_type === 'talent' ? 'Bookee' : u.user_type === 'both' ? 'Both' : 'Booker',
        `"${u.location || u.city || ''}"`,
        `"${u.referral_source || ''}"`,
        u.is_verified ? 'Verified' : 'Unverified',
        u.created_at ? new Date(u.created_at).toLocaleDateString() : ''
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bookd-users-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`Exported ${allUsers.length} users`);
    } catch (error) {
      toast.error('Export failed');
      console.error('Export error:', error);
    }
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 opacity-50" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4" /> 
      : <ArrowDown className="h-4 w-4" />;
  };

  const handleVerifyUser = async (userId, verified) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/verify?verified=${verified}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success(`User ${verified ? 'verified' : 'unverified'}`);
        loadData();
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('User deleted');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Booking status updated');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="h-6 w-6 text-primary" />
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              Admin Panel
            </h1>
          </div>
          <p className="text-muted-foreground">Manage users, bookings, and platform settings</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              <Card>
                <CardContent className="p-4 text-center">
                  <Users className="h-6 w-6 text-primary mx-auto mb-2" />
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Crown className="h-6 w-6 text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{stats.total_bookees}</p>
                  <p className="text-xs text-muted-foreground">Bookees</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <UserCheck className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{stats.total_bookers}</p>
                  <p className="text-xs text-muted-foreground">Bookers</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Calendar className="h-6 w-6 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{stats.total_bookings}</p>
                  <p className="text-xs text-muted-foreground">Bookings</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <DollarSign className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">${stats.total_revenue?.toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">Platform Revenue</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <AlertCircle className="h-6 w-6 text-orange-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{stats.pending_verifications}</p>
                  <p className="text-xs text-muted-foreground">Pending Verify</p>
                </CardContent>
              </Card>
            </div>

            {/* Additional Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Total Booking Value</p>
                  <p className="text-xl font-bold text-green-400">${stats.total_booking_value?.toFixed(2) || '0'}</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-500/10 border-blue-500/30">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Tax Collected</p>
                  <p className="text-xl font-bold text-blue-400">${stats.total_tax_collected?.toFixed(2) || '0'}</p>
                </CardContent>
              </Card>
              <Card className="bg-purple-500/10 border-purple-500/30">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">New Users (7 days)</p>
                  <p className="text-xl font-bold text-purple-400">{stats.recent_signups_7d || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-yellow-500/10 border-yellow-500/30">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">Completed Bookings</p>
                  <p className="text-xl font-bold text-yellow-400">{stats.completed_bookings || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Referral Sources */}
            {stats.referral_sources && Object.keys(stats.referral_sources).length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Where Users Are Coming From
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {Object.entries(stats.referral_sources)
                      .sort((a, b) => b[1] - a[1])
                      .map(([source, count]) => (
                        <div key={source} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                          <span className="text-sm capitalize">{source?.replace('_', ' ') || 'Unknown'}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Main Content */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-muted/50 rounded-xl p-1">
            <TabsTrigger value="users" className="rounded-lg">Users</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-lg">Bookings</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg">Settings</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            {/* Search and Export */}
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex gap-3 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 rounded-xl"
                    onKeyPress={(e) => e.key === 'Enter' && searchUsers(1)}
                  />
                </div>
                <select
                  value={userTypeFilter}
                  onChange={(e) => { setUserTypeFilter(e.target.value); }}
                  className="rounded-xl border bg-background px-3"
                >
                  <option value="">All Types</option>
                  <option value="talent">Bookees</option>
                  <option value="client">Bookers</option>
                </select>
                <Button onClick={() => searchUsers(1)} className="rounded-xl">Search</Button>
              </div>
              <Button onClick={exportToCSV} variant="outline" className="rounded-xl">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* User count */}
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * 20 + 1}-{Math.min(currentPage * 20, totalUsers)} of {totalUsers} users
            </p>

            {/* Users Table */}
            <Card>
              <CardContent className="p-0 relative">
                {pageLoading && (
                  <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium w-12">#</th>
                        <th className="text-left p-4 font-medium">
                          <button 
                            onClick={() => handleSort('name')} 
                            className="flex items-center gap-1 hover:text-primary transition-colors"
                          >
                            User <SortIcon field="name" />
                          </button>
                        </th>
                        <th className="text-left p-4 font-medium">
                          <button 
                            onClick={() => handleSort('user_type')} 
                            className="flex items-center gap-1 hover:text-primary transition-colors"
                          >
                            Type <SortIcon field="user_type" />
                          </button>
                        </th>
                        <th className="text-left p-4 font-medium">
                          <button 
                            onClick={() => handleSort('location')} 
                            className="flex items-center gap-1 hover:text-primary transition-colors"
                          >
                            Location <SortIcon field="location" />
                          </button>
                        </th>
                        <th className="text-left p-4 font-medium">
                          <button 
                            onClick={() => handleSort('referral_source')} 
                            className="flex items-center gap-1 hover:text-primary transition-colors"
                          >
                            Source <SortIcon field="referral_source" />
                          </button>
                        </th>
                        <th className="text-left p-4 font-medium">Status</th>
                        <th className="text-left p-4 font-medium">
                          <button 
                            onClick={() => handleSort('created_at')} 
                            className="flex items-center gap-1 hover:text-primary transition-colors"
                          >
                            Joined <SortIcon field="created_at" />
                          </button>
                        </th>
                        <th className="text-right p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, index) => (
                        <tr key={u.user_id} className="border-t border-border hover:bg-muted/30">
                          <td className="p-4 text-sm text-muted-foreground">
                            {(currentPage - 1) * 20 + index + 1}
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                              {u.phone && <p className="text-xs text-muted-foreground">{u.phone}</p>}
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge variant={u.user_type === 'talent' ? 'default' : 'secondary'}>
                              {u.user_type === 'talent' ? 'Bookee' : u.user_type === 'both' ? 'Both' : 'Booker'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-muted-foreground">
                              {u.location || u.city || '-'}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-muted-foreground capitalize">
                              {u.referral_source || '-'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {u.is_verified && (
                                <Badge className="bg-green-500/20 text-green-400">Verified</Badge>
                              )}
                              {u.is_admin && (
                                <Badge className="bg-purple-500/20 text-purple-400">Admin</Badge>
                              )}
                              {!u.is_verified && !u.is_admin && (
                                <Badge variant="outline">Unverified</Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-4">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleVerifyUser(u.user_id, !u.is_verified)}
                                title={u.is_verified ? 'Unverify' : 'Verify'}
                              >
                                {u.is_verified ? (
                                  <XCircle className="h-4 w-4 text-red-400" />
                                ) : (
                                  <CheckCircle className="h-4 w-4 text-green-400" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(u.user_id)}
                                title="Delete User"
                              >
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Pagination */}
            <div className="flex justify-center items-center gap-2">
              <Button
                variant="outline"
                disabled={currentPage === 1 || pageLoading}
                onClick={() => searchUsers(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="px-4 py-2">Page {currentPage} of {totalPages}</span>
              <Button
                variant="outline"
                disabled={currentPage === totalPages || pageLoading}
                onClick={() => searchUsers(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Booking ID</th>
                        <th className="text-left p-4 font-medium">Amount</th>
                        <th className="text-left p-4 font-medium">Status</th>
                        <th className="text-left p-4 font-medium">Payment</th>
                        <th className="text-left p-4 font-medium">Date</th>
                        <th className="text-right p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => (
                        <tr key={b.booking_id} className="border-t border-border">
                          <td className="p-4 font-mono text-sm">#{b.booking_id?.slice(-8)}</td>
                          <td className="p-4 font-bold text-primary">${b.total_price?.toFixed(2)}</td>
                          <td className="p-4">
                            <Badge className={
                              b.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                              b.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                              b.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }>
                              {b.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Badge variant={b.payment_status === 'paid' ? 'default' : 'outline'}>
                              {b.payment_status || 'unpaid'}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-4">
                            <select
                              value={b.status}
                              onChange={(e) => handleUpdateBookingStatus(b.booking_id, e.target.value)}
                              className="rounded border bg-background px-2 py-1 text-sm"
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Platform Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-xl bg-muted/50">
                  <h4 className="font-medium mb-2">Stripe Configuration</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Stripe is configured with test keys. Update to live keys before launch.
                  </p>
                  <Badge className="bg-green-500/20 text-green-400">Connected</Badge>
                </div>

                <div className="p-4 rounded-xl bg-muted/50">
                  <h4 className="font-medium mb-2">PayPal Configuration</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Add PAYPAL_CLIENT_ID and PAYPAL_SECRET to enable PayPal payments.
                  </p>
                  <Badge className="bg-yellow-500/20 text-yellow-400">Not Configured</Badge>
                </div>

                <div className="p-4 rounded-xl bg-muted/50">
                  <h4 className="font-medium mb-2">Stripe Identity (ID Verification)</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Enable Stripe Identity product in your Stripe dashboard to activate ID verification.
                  </p>
                  <Badge className="bg-yellow-500/20 text-yellow-400">Pending Setup</Badge>
                </div>

                <div className="p-4 rounded-xl bg-muted/50">
                  <h4 className="font-medium mb-2">Push Notifications</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Generate VAPID keys and add VAPID_PUBLIC_KEY, VAPID_PRIVATE_KEY, VAPID_EMAIL to enable push notifications.
                  </p>
                  <Badge className="bg-yellow-500/20 text-yellow-400">Not Configured</Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
